import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Modal, ModalActions, ModalContent, ModalTitleInverse, useSnackbar } from '@c2fo/liquidity';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import InvoiceEditLoadingModal from '@/enterprise/components/OfferBuilder/EditInvoices/InvoiceEditLoadingModal';
import useInvoiceEdits, { EditError } from '@/enterprise/components/OfferBuilder/EditInvoices/useInvoiceEdits';
import useDeleteRecurringRule from '@/enterprise/data/useDeleteRecurringRule';
import { TakerMarketWithRecurringRule } from '@/enterprise/data/useRecurringRules';
import { useReporting } from '@/reporting';
import useExchangeRates from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import useLocaleFormat from '@/utils/useLocaleFormat';
import InvoiceReinclusion, { InvoiceReinclusionProps } from './InvoiceReinclusion';

interface DeleteAllRulesModalProps {
  hasFilterOrSearch: boolean;
  onClose: () => void;
  onSuccess: () => void;
  open: boolean;
  recurringRules: TakerMarketWithRecurringRule[];
}

const DeleteAllRulesModal = ({
  hasFilterOrSearch,
  onClose,
  onSuccess,
  open,
  recurringRules,
}: DeleteAllRulesModalProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const showSnackbar = useSnackbar();
  const { asNumber, asCurrency } = useLocaleFormat();
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [contactSRM, setContactSRM] = useState(false);
  const [handleInvoices, setHandleInvoices] = useState<'include' | 'exclude'>('include');
  const { mutateAsync: deleteRecurringRule } = useDeleteRecurringRule();
  const { data: exchangeRates = {} } = useExchangeRates();
  const { selectedCurrency } = useCurrencyContext();
  const [includeInvoicesLoading, setIncludeInvoicesLoading] = useState(false);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [errors, setErrors] = useState<EditError[]>([]);
  const { handleIncludeAllInvoices } = useInvoiceEdits({
    onLoadingChange: (isLoading) => {
      setIncludeInvoicesLoading(isLoading);
      if (!isLoading) {
        setProgress(null);
      }
    },
    onProgressChange: (current, total) => setProgress({ current, total }),
    onError: (editErrors) => setErrors(editErrors),
    subscribeToMarketStats: true,
  });
  const excludedInvoicesCount = recurringRules.reduce(
    (acc, { takerMarket }) => acc + takerMarket.takerExcludedInvoiceCount,
    0
  );
  const excludedInvoicesAmount = asCurrency(
    recurringRules.reduce(
      (acc, { takerMarket }) =>
        acc +
        convertCurrency({
          amount: takerMarket.takerExcludedInvoiceAmount,
          from: takerMarket.currency,
          to: selectedCurrency,
          exchangeRates,
        }),
      0
    ),
    selectedCurrency
  );

  const handleClose = () => {
    setContactSRM(false);
    setHandleInvoices('include');
    onClose();
  };

  const handleInvoicesChange = (value: 'include' | 'exclude') => {
    setHandleInvoices(value);
  };

  const deleteAllRules = async () => {
    setLoading(true);
    trackEnterpriseEvent('recurring-rules::delete::invoice-reinclusion', { type: handleInvoices, ruleId: 'allRules' });

    const items = recurringRules
      .filter(({ rule }) => rule?.id)
      .map(({ rule }) => ({
        id: rule.id,
        makerOrganizationUuid: rule.makerOrganizationUuid,
        marketId: rule.marketId,
        takerId: rule.takerId,
        takerUuid: rule.takerUuid,
        marketUuid: rule.marketUuid,
      }));

    await deleteRecurringRule(items, {
      onSuccess: () => {
        setLoading(false);
        onSuccess();
        handleClose();
        showSnackbar({ message: t('automation.deleteAllRulesSuccess') });
      },
      onError: () => {
        setLoading(false);
        setShowError(true);
      },
    });

    if (handleInvoices === 'include' && excludedInvoicesCount > 0) {
      const includeTakerMarkets = recurringRules
        .map(({ takerMarket }) => takerMarket)
        .filter((tm) => tm.takerExcludedInvoiceCount > 0);

      await handleIncludeAllInvoices(includeTakerMarkets);
    }

    return;
  };

  const handleCloseAfterError = () => {
    setErrors([]);
    setLoading(false);
    onClose();
  };

  const invoiceReinclusionProps = {
    excludedInvoicesCount,
    excludedInvoicesAmount,
    handleInvoices,
    handleInvoicesChange,
  } satisfies InvoiceReinclusionProps;

  return (
    <>
      <Modal disableOutsideClickClose={loading} onClose={onClose} open={open} size="sm">
        <ModalTitleInverse>
          {hasFilterOrSearch ? t('automation.deleteAllFilteredInvoiceRules') : t('automation.deleteAllInvoiceRules')}
        </ModalTitleInverse>
        <ModalContent className="space-y-4">
          {showError && (
            <>
              {contactSRM ? (
                <Alert
                  description={t('core.contactSrm.description')}
                  full
                  title={t('core.contactSrm.title')}
                  type="info"
                />
              ) : (
                <Alert
                  action={{
                    onClick: () => {
                      // fire track event for slack channel
                      trackEnterpriseEvent('recurring-rules::error', { type: 'delete', ruleId: 'allRules' });
                      setContactSRM(true);
                    },
                    text: t('core.contactSrm'),
                  }}
                  description={t('core.reachOutForAssistance')}
                  full
                  title={t('recurringRules.deleteRulesError')}
                  type="error"
                />
              )}
            </>
          )}
          <div>
            {hasFilterOrSearch
              ? t('automation.deleteAllFilteredInvoiceRulesWarning')
              : t('automation.deleteAllInvoiceRulesWarning')}
          </div>
          <ul className="ml-4 list-disc space-y-1">
            <li>{t('recurringRules.deleteParticipationDescription')}</li>
            <li>{t('recurringRules.deleteFutureInvoicesDescription')}</li>
          </ul>
          <InvoiceReinclusion {...invoiceReinclusionProps} />
        </ModalContent>
        <ModalActions>
          <Button onClick={onClose} variant="secondary">
            {t('core.cancel')}
          </Button>
          <Button loading={loading} onClick={deleteAllRules} variant="destructive">
            {`${t('core.delete')} (${asNumber({ value: recurringRules.length })})`}
          </Button>
        </ModalActions>
      </Modal>
      <InvoiceEditLoadingModal
        errors={errors}
        onClearErrors={() => setErrors([])}
        onClose={handleCloseAfterError}
        open={includeInvoicesLoading}
        progress={progress}
      />
    </>
  );
};

export default DeleteAllRulesModal;
